import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { SignalFormField } from '../../core/functions/signal-form-field';
import {
  alphaOnly,
  composeValidators,
  email,
  exactLength,
  matchesField,
  numeric,
  phone,
  required
} from '../../core/functions/validators';
import { SignalInputComponent } from '../../shared/components/signal-input/signal-input.component';
import { DisplayErrorsComponent } from '../../shared/components/display-errors/display-errors.component';
import { LoadingStatus } from './util/loading-status.enum';
import { ProviderApplicationService } from './services/provider-application.service';
import { formatProviderApplicationError } from './util/provider-application-error';

@Component({
  selector: 'app-providers-application',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    SignalInputComponent,
    DisplayErrorsComponent
  ],
  templateUrl: './providers-application.component.html',
  styleUrl: './providers-application.component.scss'
})
export class ProvidersApplicationComponent {
  loading = signal(false);
  showForm = signal(true);

  firstName = SignalFormField<string>('', composeValidators(required(), alphaOnly()));
  lastName = SignalFormField<string>('', composeValidators(required(), alphaOnly()));
  groupNpi = SignalFormField<string>('', composeValidators(required(), exactLength(10), numeric('NPI must be numeric')));
  individualNpi = SignalFormField<string>('', composeValidators(required(), exactLength(10), numeric('NPI must be numeric')));
  emailAddress = SignalFormField<string>('', composeValidators(required(), email()));

  emailAddress2 = SignalFormField<string>('', composeValidators(
    required(),
    email(),
    matchesField(() => this.emailAddress, 'E-mail Addresses must match')
  ));

  phoneNumber = SignalFormField<string>(
    '',
    composeValidators(required(), phone()),
    (value: string) => {
      const digits = value.replace(/\D/g, '').slice(0, 10);

      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    },
    (displayValue: string) => displayValue.replace(/\D/g, '').slice(0, 10)
  );

  clinicName = SignalFormField<string>('', composeValidators(required()));
  address = SignalFormField<string>('', composeValidators(required()));
  address2 = SignalFormField<string>('');
  city = SignalFormField<string>('', composeValidators(required()));
  state = SignalFormField<string>('', composeValidators(required(), exactLength(2)));
  zip = SignalFormField<string>(
    '',
    composeValidators(required(), exactLength(5), numeric()),
    undefined,
    (displayValue: string) => displayValue.replace(/\D/g, '').slice(0, 5)
  );

  loadingStatus: LoadingStatus = LoadingStatus.IDLE;
  submitMessage = '';

  constructor(private providerApplicationService: ProviderApplicationService) {}

  async onSubmit(): Promise<void> {
    if (!this.isFormValid()) {
      this.markAllFieldsTouched();
      return;
    }

    this.loading.set(true);
    this.loadingStatus = LoadingStatus.LOADING;

    try {
      await firstValueFrom(
        this.providerApplicationService.submitApplication(this.formData())
      );

      this.showMessage(
        'Thanks for registering! Please check the email you provided for next steps in the onboarding process. If you don’t see it, be sure to check your spam or junk folder.',
        LoadingStatus.SUCCESS
      );

      this.resetForm();
      this.showForm.set(false);
    } catch (error: unknown) {
      this.showMessage(
        formatProviderApplicationError(error, {
          groupNpi: this.groupNpi.value(),
          individualNpi: this.individualNpi.value(),
          emailAddress: this.emailAddress.value()
        }),
        LoadingStatus.ERROR
      );
    } finally {
      this.loading.set(false);
    }
  }

  isFormValid = (): boolean => (
    this.firstName.valid() &&
    this.lastName.valid() &&
    this.address.valid() &&
    this.city.valid() &&
    this.state.valid() &&
    this.zip.valid() &&
    this.groupNpi.valid() &&
    this.individualNpi.valid() &&
    this.phoneNumber.valid() &&
    this.emailAddress.valid() &&
    this.emailAddress2.valid() &&
    this.clinicName.valid()
  );

  private showMessage(message: string, status: LoadingStatus): void {
    this.submitMessage = message;
    this.loadingStatus = status;
  }

  private resetForm(): void {
    this.formFields().forEach(field => field.reset());
  }

  private markAllFieldsTouched(): void {
    this.formFields().forEach(field => field.setTouched());
  }

  private formFields() {
    return [
      this.firstName,
      this.lastName,
      this.address,
      this.address2,
      this.city,
      this.state,
      this.zip,
      this.groupNpi,
      this.individualNpi,
      this.phoneNumber,
      this.emailAddress,
      this.emailAddress2,
      this.clinicName
    ];
  }

  private formData = () => ({
    firstName: this.firstName.value(),
    lastName: this.lastName.value(),
    address: this.address.value(),
    address2: this.address2.value(),
    city: this.city.value(),
    state: this.state.value(),
    zip: this.zip.value(),
    groupNpi: this.groupNpi.value(),
    individualNpi: this.individualNpi.value(),
    phoneNumber: this.phoneNumber.value(),
    emailAddress: this.emailAddress.value(),
    clinicName: this.clinicName.value()
  });

  get isSubmitting(): boolean {
    return this.loadingStatus === LoadingStatus.LOADING;
  }

  get submitSuccess(): boolean {
    return this.loadingStatus === LoadingStatus.SUCCESS;
  }

  get submitError(): boolean {
    return this.loadingStatus === LoadingStatus.ERROR;
  }
}
